import { faBorderAll, faLayerGroup, faMapLocationDot, type IconDefinition } from "@fortawesome/free-solid-svg-icons";
import type { ViewToggle } from "./viewPrefs";

/**
 * **The VIEW toggles** (`decisions/0191` Ruling 4, `decisions/0203` point 9): the viewer's own display
 * toggles, each with a key. They sit in the tool rail's VIEW section as toggle buttons (`BoardToolRail`);
 * the top bar's VIEW menu that held them retired with the bar (`website-shell` 4.3). Rows are entries in
 * {@link VIEW_ROWS}, so a later toggle is one more entry. Keys go through the root listener
 * (`useCombatKeyboard.ts`) with the tool rail's.
 */

/** The toggles a row reads and flips. `CombatApp` builds it. */
export type ViewContext = { grid: ViewToggle; mapKey: ViewToggle; terrainMechanics: ViewToggle };

export type ViewRow = {
  id: string;
  label: string;
  icon: IconDefinition;
  /** The bare key that flips it, lower-case. */
  key: string;
  modes: readonly ("edit" | "play")[];
  toggleOf: (ctx: ViewContext) => ViewToggle;
};

/** In the rail's order: G, O, K (the picked prototype's VIEW section). */
export const VIEW_ROWS: readonly ViewRow[] = [
  // The full grid over the terrain. PLAY only: EDIT always shows the authoring grid.
  { id: "grid", label: "Grid", icon: faBorderAll, key: "g", modes: ["play"], toggleOf: (ctx) => ctx.grid },
  // Each piece of same-mechanics terrain outlined and labelled (`terrain-mechanics-overlay`).
  { id: "terrain-mechanics", label: "Terrain mechanics", icon: faLayerGroup, key: "o", modes: ["edit", "play"], toggleOf: (ctx) => ctx.terrainMechanics },
  { id: "map-key", label: "Map key", icon: faMapLocationDot, key: "k", modes: ["edit", "play"], toggleOf: (ctx) => ctx.mapKey },
];

export function viewRowsFor(mode: "edit" | "play", rows: readonly ViewRow[] = VIEW_ROWS): ViewRow[] {
  return rows.filter((r) => r.modes.includes(mode));
}

/** The row a bare key press flips in `mode`, if any (case-insensitive). */
export function viewRowForKey(key: string, mode: "edit" | "play", rows: readonly ViewRow[] = VIEW_ROWS): ViewRow | undefined {
  const k = key.toLowerCase();
  return viewRowsFor(mode, rows).find((r) => r.key === k);
}
